import { OrganizationList } from '@/components'
import { usePositionStorage } from '@/hooks'
import { AuthError, safeRedirectPath, useAuth } from '@/lib'
import { authSelectOrganisation } from '@jobilla/nexus-sdk'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logger } from '../../utilities'
import { AuthDivider } from './auth-divider'
import { AuthErrorAlert } from './auth-error-alert'
import { AuthInput } from './auth-input'
import { AuthSubmitButton } from './auth-submit-button'
import { EmailVerificationForm } from './email-verification-form'
import { GoogleSignInButton } from './google-sign-in-button'
import { JobillaBrandLogo } from './jobilla-brand-logo'

interface OrgSelectionState {
	pendingAuthenticationToken: string
	organizations: Array<{ id: string; name: string }>
}

interface EmailVerificationState {
	email: string
	pendingAuthenticationToken: string
}

export function SignInForm() {
	const { signIn, isLoading: isAuthLoading } = useAuth()
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const { redirect: redirectTo } = useSearch({ strict: false }) as { redirect?: string }
	const { getRedirect, clearRedirect } = usePositionStorage()
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [orgSelection, setOrgSelection] = useState<OrgSelectionState | null>(null)
	const [verification, setVerification] = useState<EmailVerificationState | null>(null)

	function redirectAfterAuth(defaultPath?: string) {
		const pendingRedirect = redirectTo || getRedirect()
		clearRedirect()
		window.location.href = safeRedirectPath(pendingRedirect, defaultPath ?? '/company')
	}

	async function handleSubmit(event: FormEvent) {
		event.preventDefault()
		setError(null)
		setIsLoading(true)

		try {
			await signIn(email.trim(), password)
			redirectAfterAuth()
		} catch (err) {
			if (!(err instanceof AuthError)) {
				logger.error(err, { category: 'auth', data: { action: 'sign-in' } })
				setError(t('auth.signIn.errorDefault'))
				return
			}

			const challenge = err.data && 'code' in err.data ? err.data : null

			if (
				challenge?.code === 'organization_selection_required'
				&& challenge.pending_authentication_token
				&& challenge.organizations
			) {
				setOrgSelection({
					pendingAuthenticationToken: challenge.pending_authentication_token,
					organizations: challenge.organizations,
				})
			} else if (
				challenge?.code === 'email_verification_required'
				&& challenge.pending_authentication_token
				&& challenge.email
			) {
				logger.warn('Sign-in requires email verification', {
					category: 'auth',
					data: { code: challenge.code, status: err.status },
				})
				setVerification({
					email: challenge.email,
					pendingAuthenticationToken: challenge.pending_authentication_token,
				})
			} else {
				setError(err.message)
			}
		} finally {
			setIsLoading(false)
		}
	}

	if (verification) {
		return (
			<EmailVerificationForm
				email={verification.email}
				pendingAuthenticationToken={verification.pendingAuthenticationToken}
			/>
		)
	}

	if (orgSelection) {
		return (
			<OrganizationList
				organizations={orgSelection.organizations}
				onSelect={(org) => {
					void (async () => {
						try {
							const { data } = await authSelectOrganisation({
								pendingAuthenticationToken: orgSelection.pendingAuthenticationToken,
								organisationId: org.id,
							}, { withCredentials: true })
							const slug = data.organisationSlug
							redirectAfterAuth(slug ? `/company/${slug}` : undefined)
						} catch {
							setOrgSelection(null)
							setError(t('auth.signIn.errorDefault'))
						}
					})()
				}}
			/>
		)
	}

	return (
		<div className="w-full max-w-2xl mx-auto p-8">
			<JobillaBrandLogo />

			<div className="mt-8">
				<h1 className="text-3xl font-semibold text-gray-800 tracking-[0.01em]">{t('auth.signIn.title')}</h1>
			</div>

			<form onSubmit={handleSubmit} className="mt-8 space-y-8">
				<AuthErrorAlert message={error} />

				<AuthInput
					label={t('auth.signIn.emailLabel')}
					type="email"
					placeholder={t('auth.signIn.emailPlaceholder')}
					value={email}
					onChange={setEmail}
					required={true}
					autoComplete="email"
				/>

				<AuthInput
					label={t('auth.signIn.passwordLabel', 'Password')}
					type="password"
					placeholder=""
					value={password}
					onChange={setPassword}
					required={true}
					autoComplete="current-password"
				/>

				<div className="flex justify-end">
					<Link to="/reset-password" className="text-sm text-[#6366F1] hover:text-[#4F46E5] font-medium">
						{t('auth.signIn.forgotPassword', 'Forgot password?')}
					</Link>
				</div>

				<AuthSubmitButton isLoading={isLoading || isAuthLoading}>
					{t('auth.signIn.submit')}
				</AuthSubmitButton>

				<AuthDivider />

				<GoogleSignInButton />

				<p className="text-center text-sm text-gray-600">
					{t('auth.signIn.noAccount')}{' '}
					<Link
						to="/signup"
						search={redirectTo ? { redirect: redirectTo } : {}}
						className="text-[#6366F1] hover:text-[#4F46E5] font-medium"
					>
						{t('auth.signIn.signUpLink')}
					</Link>
				</p>
			</form>
		</div>
	)
}
